@if (isBlocked()) {
  <div class="flex items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-3">
    <mat-icon class="text-red-600">block</mat-icon>
    <h2 class="m-0 text-base font-bold text-red-800">
      {{ 'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.BLOCKED_TITLE' | t }}
    </h2>
  </div>
} @else {
  <div class="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-3">
    <mat-icon class="text-amber-600">warning</mat-icon>
    <h2 class="m-0 text-base font-bold text-amber-800">
      {{ 'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.TITLE' | t }}
    </h2>
  </div>
}

<mat-dialog-content class="flex min-h-[160px] flex-col gap-4 p-4">
  @if (isBlocked()) {
    <p class="m-0 text-sm text-gray-700">
      @if (isUnverified()) {
        {{ 'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.UNVERIFIED_BODY' | t }}
      } @else {
        {{ 'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.BLOCKED_BODY' | t }}
      }
    </p>
  } @else {
    <div class="flex flex-col gap-2 text-sm text-gray-700">
      @if (data.reason === 'logistic-unit-changed') {
        <p class="m-0">
          {{ 'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.INTRO_LOGISTIC_UNIT' | t }}
        </p>
      } @else if (data.reason === 'batch-control-off') {
        <p class="m-0">
          {{ 'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.INTRO_BATCH_CONTROL_OFF' | t }}
        </p>
      } @else {
        <p class="m-0">
          {{ 'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.INTRO_LABEL_SELECTION_OFF' | t }}
        </p>
      }
      <ul class="m-0 flex list-disc flex-col gap-1 pl-5">
        <!-- La selección pasa a automática cuando se apaga el flag, directamente o arrastrado por
             el control por lote; cambiar la unidad no altera quién elige las etiquetas. -->
        @if (data.reason !== 'logistic-unit-changed') {
          <li>{{ 'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.EFFECT_APP' | t }}</li>
        }
        <li>{{ 'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.EFFECT_UNIT' | t }}</li>
      </ul>
    </div>
  }

  @if (isLoading()) {
    <div class="flex flex-col items-center justify-center gap-2 py-6">
      <mat-spinner diameter="32"></mat-spinner>
      <span class="text-sm text-gray-500">
        {{ 'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.LOADING' | t }}
      </span>
    </div>
  } @else if (lookup().available) {
    @if (hasDeliveries()) {
      <div class="flex flex-col gap-3">
        <!-- La lista está topeada a una página, así que `total` es un PISO una vez que el tope
             cortó: decimos "más de N" en vez de hacer pasar el tamaño de página por la cuenta. -->
        <p class="m-0 text-sm font-semibold text-gray-800">
          @if (lookup().truncated) {
            {{
              'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.AFFECTED_TITLE_TRUNCATED'
                | t: { count: lookup().total }
            }}
          } @else {
            {{
              'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.AFFECTED_TITLE'
                | t: { count: lookup().total }
            }}
          }
        </p>
        <div class="flex max-h-[45vh] flex-col gap-3 overflow-y-auto pr-1">
          @for (group of lookup().groups; track group.tripId) {
            <div class="rounded border border-gray-200">
              <div
                class="flex items-center gap-1.5 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-600"
              >
                <mat-icon class="icon-size-4">local_shipping</mat-icon>
                @if (group.tripId !== null) {
                  <span>
                    {{
                      'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.TRIP'
                        | t: { tripId: group.tripId }
                    }}
                  </span>
                } @else {
                  <span>
                    {{ 'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.NO_TRIP' | t }}
                  </span>
                }
              </div>
              <ul class="m-0 flex list-none flex-col p-0">
                @for (delivery of group.deliveries; track delivery._id) {
                  <li
                    class="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0"
                  >
                    <span class="font-mono text-xs">{{ delivery.serial }}</span>
                    <span class="text-xs text-gray-500">
                      {{ (delivery.deliveryDateTime | date: 'dd-MM-yyyy HH:mm') || '—' }}
                    </span>
                  </li>
                }
              </ul>
            </div>
          }
        </div>
        @if (lookup().truncated) {
          <p class="m-0 text-xs italic text-gray-500">
            {{
              'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.TRUNCATED_HINT'
                | t: { count: lookup().total }
            }}
          </p>
        }
      </div>
    } @else {
      <p class="m-0 text-sm text-gray-500">
        {{ 'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.EMPTY' | t }}
      </p>
    }
  }
</mat-dialog-content>

<mat-dialog-actions class="flex justify-end gap-2 border-t border-gray-200 p-4">
  @if (isBlocked()) {
    <!-- Closing a blocked change always reverts the control; there is no way to continue. -->
    <button mat-raised-button color="primary" type="button" (click)="cancel()">
      {{ 'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.CLOSE' | t }}
    </button>
  } @else {
    <button mat-stroked-button type="button" (click)="cancel()">
      {{ 'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.CANCEL' | t }}
    </button>
    <button
      mat-raised-button
      color="warn"
      type="button"
      [disabled]="isLoading()"
      (click)="confirm()"
    >
      {{ 'MATERIAL.LOGISTIC_CONTROL.LABEL_SELECTION_IMPACT.CONFIRM' | t }}
    </button>
  }
</mat-dialog-actions>
